import {Component, HostListener, Inject, OnDestroy} from '@angular/core'
import {ActivatedRoute, Router} from '@angular/router'
import {OptableLayout} from '../../../../core/components/layout/optable-layout.interface'
import {faChevronRight} from '@fortawesome/pro-regular-svg-icons/faChevronRight'
import {HasStructuredData, PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {BehaviorSubject, combineLatest, EMPTY, mergeMap, Observable, Subject, zipWith} from 'rxjs'
import {map} from 'rxjs/operators'
import {LinkService} from '../../../../core/i18n/link.service'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {ArticlePage_SearchArticlesGql} from '../../../../../__generated__/types'
import {Breadcrumb} from '../../../../craft/breadcrumbs/breadcrumbs.component'
import {ArticlePage, ArticlePreview, TopicListItem} from '../../../../core/content/types/article.types'
import {Article as ArticleSchema, WithContext} from 'schema-dts'
import {TranslocoService} from '@ngneat/transloco'
import {Block} from 'datocms-structured-text-utils'
import {DatoRichTextBlock} from '../../../../core/content/types/rich-text.types'
import {StructuredTextPlotFieldsFragment} from '../../../../../__generated__/datocms.types'
import {DocumentService} from '../../../../craft/utils/document.service'
import {DOCUMENT} from '@angular/common'
import {TableOfContent} from './article-inline-table-of-content/article-inline-table-of-content.component'
import {transformDatocmsImageUrl} from '../../../../core/picture/loaders/datocms'
import {ArticleContentService} from '../../../../core/content/services/article-content.service'
import {parseArticlePreview} from '../../../../core/content/serde/article-page.serde'
import {Language} from '../../../../core/i18n/language'

@Component({
  templateUrl: './article-page.component.html',
})
export class ArticlePageComponent
  extends PageComponent
  implements HasStructuredData<ArticleSchema>, OptableLayout, OnDestroy
{
  private readonly resolved = this.route.snapshot.data['content'] as ArticlePage
  private readonly articleSubject: BehaviorSubject<ArticlePage>
  protected readonly article$: Observable<ArticlePage>
  protected readonly topics$: Observable<TopicListItem[]>
  protected readonly breadcrumbs$: Observable<Breadcrumb[]>
  protected readonly tableOfContent$: Observable<TableOfContent>

  // OptableLayout
  readonly optOutOfLayout = true

  showMobileCta = false

  protected readonly arrowIcon = faChevronRight
  private readonly unsubscribe$ = new Subject<void>()

  // contact form variables
  protected readonly dataPrivacyLink = this.linkService.localizedPathTo(PUBLIC_ROUTES.DATA_PRIVACY)
  protected readonly articles$: Observable<ArticlePreview[]>
  menuData$: Observable<{topics: TopicListItem[]; articles: ArticlePreview[]}>

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly linkService: LinkService,
    private readonly documentService: DocumentService,
    private readonly contentService: ArticleContentService,
    private readonly searchArticlesGql: ArticlePage_SearchArticlesGql,
    @Inject(DOCUMENT) readonly document: Document,
    localeService: TranslocoService,
  ) {
    super()

    this.articleSubject = new BehaviorSubject<ArticlePage>({...this.resolved})
    this.article$ = this.articleSubject.asObservable()
    this.topics$ = this.contentService.getTopicList()
    this.articles$ = this.article$.pipe(
      mergeMap(article => {
        if (article.topic?.slug) {
          return this.searchArticlesGql.fetch({topic: article.topic?.slug, page: {offset: 0, limit: 30}})
        }

        return EMPTY
      }),
      map(result => result.data.searchArticles.items.map(a => parseArticlePreview(a, this.linkService))),
    )
    this.menuData$ = combineLatest([this.topics$, this.articles$]).pipe(
      map(([topics, articles]) => ({topics, articles})),
    )

    this.tableOfContent$ = this.article$.pipe(map(article => this.generateTableOfContent(article)))
    this.breadcrumbs$ = this.article$.pipe(
      map(article => {
        // custom parent, ignore default breadcrumbs
        if (article.parent) {
          return [
            {name: article.parent.breadcrumbTitle || article.parent.title, link: article.parent.path},
            {name: article.breadcrumbTitle || article.title, link: this.router.url},
          ]
        } else {
          const breadcrumbs: Breadcrumb[] = []
          breadcrumbs.push({
            name: localeService.translate('article.overview.title'),
            link: linkService.localizedPathTo(PUBLIC_ROUTES.ARTICLE_OVERVIEW),
          })
          if (article.topic) {
            breadcrumbs.push({name: article.topic.breadcrumbTitle || article.topic.title, link: article.topic.path})
          }
          breadcrumbs.push({name: article.breadcrumbTitle || article.title, link: this.router.url})
          return breadcrumbs
        }
      }),
    )
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event) {
    this.showMobileCta = event.target?.defaultView?.scrollY > 400
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.article$.pipe(
      map(article => {
        const metadata: PageMetadata = {
          title: article.meta?.title || article.title,
          description: article.meta?.description || article.preview,
          imageUrl: article.headerImage?.url
            ? transformDatocmsImageUrl(article.headerImage.url, {width: 640})
            : undefined,
          type: 'article',
          doNotIndexPage: article.meta?.doNotIndex,
          canonicalUrl: article.meta?.canonicalUrl,
        }
        return metadata
      }),
      zipWith(this.getStructuredData$()),
      map(([metadata, structuredData]) => ({...metadata, structuredData})),
    )
  }

  getStructuredData$(): Observable<WithContext<ArticleSchema> | undefined> {
    return this.article$.pipe(
      map(article => {
        if (article.lastPublishedAt) {
          // TODO maybe use a org schema when there are no authors?
          const authors: Pick<ArticleSchema, 'author'> = {
            author:
              article.authors.length > 0
                ? article.authors.map(author => ({
                    '@type': 'Person',
                    name: author.name,
                  }))
                : undefined,
          }
          return {
            '@context': 'https://schema.org',
            '@type': 'Article',
            ...authors,
            datePublished: article.releasedAt?.toISOString(),
            dateModified: article.lastPublishedAt?.toISOString(),
            headline: article.title,
            image: article.headerImage?.url
              ? transformDatocmsImageUrl(article.headerImage.url, {width: 640})
              : undefined,
          }
        }

        return undefined
      }),
    )
  }

  getPhoneNumberFromCta(article: ArticlePage): string | undefined {
    if (article.mainCallToAction?.type === 'Phone') {
      return article.mainCallToAction.value.toString()
    }

    if (article.secondaryCallToAction?.type === 'Phone') {
      return article.secondaryCallToAction.value.toString()
    }

    return undefined
  }

  scrollToContent(args: {id: string; scrollOffset: number}): void {
    const {id, scrollOffset} = args
    const element = this.document.getElementById(id)
    if (element) {
      this.documentService.scrollToElement(element, {topOffset: scrollOffset})
    }
  }

  private generateTableOfContent(article: ArticlePage): TableOfContent {
    const document = article.content?.json?.document
    const blocks = article.content.blocks
    if (!document || !blocks) {
      return []
    }

    // the table of content is generated based on the blocks "structured text plot field" of the article content
    return (
      document.children
        // find the plots
        .filter((child): child is Block => child.type === 'block')
        .map(block => blocks.get(block.item))
        .filter((block): block is DatoRichTextBlock => !!block)
        .filter((block): block is StructuredTextPlotFieldsFragment => block.__typename === 'StructuredTextPlotRecord')
        // filter out potentially empty ones
        .filter((p): p is Required<StructuredTextPlotFieldsFragment> => !!p.title)
    )
  }

  getPageAvailableLanguages(): Observable<Language[]> {
    return this.article$.pipe(map(article => article.activeLocales))
  }
}
